import type {
  MentorProfileChangeRequest,
  MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import {
  isReviewSection,
  PROFILE_SECTIONS,
  sectionValue,
  type ReviewSection,
} from "@/lib/mentor-profile/profile-sections";

function canonicalize(value: unknown): unknown {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? null : value.map(canonicalize);
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).map(([key, entry]) => [
      key,
      canonicalize(entry),
    ]);
    return entries.every(([, entry]) => entry == null)
      ? null
      : Object.fromEntries(entries);
  }
  return value;
}

function normalize(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

export function isSectionUpdated(
  draft: MentorProfileDraft,
  baseline: MentorProfileDraft,
  section: ReviewSection,
): boolean {
  return (
    normalize(sectionValue(draft, section)) !==
    normalize(sectionValue(baseline, section))
  );
}

const PENDING_REVIEW_STATUSES = new Set(["submitted", "in_review"]);

export function pendingProfileChangeCount(
  review:
    | {
        status: string;
        draft: MentorProfileDraft;
        published_snapshot?: MentorProfileDraft | null;
      }
    | null
    | undefined,
  live: MentorProfileDraft,
): number {
  if (!review) return 0;
  if (!PENDING_REVIEW_STATUSES.has(review.status)) return 0;
  const baseline = review.published_snapshot ?? live;
  return changedSectionLabels(review.draft, baseline).length;
}

export function changedSectionLabels(
  draft: MentorProfileDraft,
  baseline: MentorProfileDraft,
): string[] {
  return PROFILE_SECTIONS.filter((section) =>
    isSectionUpdated(draft, baseline, section.reviewSection),
  ).map((section) => section.label);
}

export function flaggedSectionsAddressed(
  draft: MentorProfileDraft,
  baseline: MentorProfileDraft,
  requests: MentorProfileChangeRequest[],
): boolean {
  const open = requests.filter((request) => request.status === "open");
  if (open.length === 0) return true;

  return open.every((request) => {
    if (!isReviewSection(request.section)) return false;
    return isSectionUpdated(draft, baseline, request.section);
  });
}
